/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  setSearchAnimationColourToMatchTheme,
  setStandardAnimationColourToMatchTheme,
} from '../../Helpers/lottieAnimations';
import { isNavigationBlockedOnPage } from '../../Hooks/useNavigation/useNavigation.helpers';
import {
  setBlockedDestinationPage,
  setShouldNavigateBackward,
  setShouldNavigateForward,
} from '../../Lib/redux/slices/navigationControls';
import makeLoaderStyling from './makeLoaderStyling';

const DEFAULT_LOADER_DURATION = 4100;

/**
 * Loader
 * @param duration of the spinner
 * @param slideDirection of the component on the page i.e. left | right
 * @param text that appears under the loader
 * @param height of the Element
 * @param componentId
 * @returns {Element}
 * @description a UI element with loading spinner that will conditionally transition the page after a period of time
 */
const Loader = ({ duration, slideDirection, text, height, componentId }) => {
  const durationOrDefault = duration || DEFAULT_LOADER_DURATION;
  const { enterPhase } = useSelector((store) => store.transition);
  const theme = useTheme();
  const { isRendererAvailable, loaderConfig } = useSelector(
    (store) => store.lottieAnimationData
  );
  const configForLoader = useRef(loaderConfig[componentId]);
  const animationJson = useRef(configForLoader.current?.animationJson);
  const styling = makeLoaderStyling({
    animationType: configForLoader.current?.animationType,
    height,
    theme,
  });

  // TODO: There is logic related to the clearing of navigation blocks within the loader component. This is in addition to similar behaviour in the Navigation Bar but it needs to be repeated here as that component does not show when loaders are present. We should move this logic to a non presentational component/hook in order to keep it in one place.
  const { pagesWithNavigationBlocks, blockedDestinationPage } = useSelector(
    (store) => store.navigationControls
  );
  const isDestinationPageBlocked = !!(
    blockedDestinationPage &&
    isNavigationBlockedOnPage(blockedDestinationPage, pagesWithNavigationBlocks)
  );
  const dispatch = useDispatch();
  const [standardLoaderDurationComplete, setStandardLoaderDurationComplete] =
    useState(false);

  useEffect(() => {
    if (
      !enterPhase &&
      slideDirection !== 'right' &&
      animationJson.current &&
      isRendererAvailable
    ) {
      /* This setTimeout below ensures that the rendering of the animation is completed before updating the
       * animation colour, but with minimal latency. It leverages JavaScript's event loop scheduling mechanism.
       * To find out more read: https://stackoverflow.com/questions/779379/why-is-settimeoutfn-0-sometimes-useful
       */
      if (configForLoader.current?.animationType === 'standard') {
        setTimeout(() => setStandardAnimationColourToMatchTheme(theme), 0);
      } else {
        setTimeout(() => setSearchAnimationColourToMatchTheme(theme), 0);
      }
    }
  }, [enterPhase]);

  useEffect(() => {
    if (enterPhase) {
      return;
    }

    if (slideDirection === 'right') {
      // i.e. click back
      dispatch(setShouldNavigateBackward({ shouldNavigateBackward: true }));
    } else {
      // i.e. click continue
      const timeoutId = setTimeout(() => {
        // Do not trigger dispatch directly to prevent multiple dispatches through remounts.
        setStandardLoaderDurationComplete(true);
      }, durationOrDefault);
      return () => clearTimeout(timeoutId);
    }
  }, [enterPhase]);

  useEffect(() => {
    if (standardLoaderDurationComplete && !isDestinationPageBlocked) {
      if (blockedDestinationPage) {
        batch(() => {
          dispatch(
            setBlockedDestinationPage({
              pageNumber: null,
            })
          );
          dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
        });
      } else {
        dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
      }
    }
  }, [standardLoaderDurationComplete, isDestinationPageBlocked]);

  const isAbleToShowLottieAnimation =
    animationJson.current && isRendererAvailable;

  return (
    <div css={styling.wrapper} data-testid="loaderContainer">
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="flex-end"
        css={styling.container}
      >
        {isAbleToShowLottieAnimation && (
          <Box css={styling.lottieLoader}>
            <lottie-player
              data-testid="mvfLottieAnimationStaticLoader"
              src={animationJson.current}
              background="transparent"
              speed="1"
              style={{
                opacity: 0 /* invisible until setSearchAnimationColourToMatchTheme runs */,
                height:
                  '150px' /* just a default value set to the mid-point of all media queries */,
              }}
              autoplay
              loop
            />
          </Box>
        )}
        <Typography css={styling.message}>{text}</Typography>
        {!isAbleToShowLottieAnimation && (
          <Box css={styling.fallbackLoader}>
            <CircularProgress size={60} color="primary" />
          </Box>
        )}
      </Grid>
    </div>
  );
};

export default Loader;
